import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  X,
  Save,
  Upload,
  AlertTriangle,
  ImageIcon,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { RecipeLink } from "./RecipeLink";

const emptyForm = () => ({
  name: "",
  image: "",
  description: "",
  price: "",
  category: "",
  is_published: true,
  quote_only: false,
  options: [],
});

const _uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `uid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const emptyGroup = () => ({
  _uid: _uid(),
  label: "",
  values: [{ _uid: _uid(), label: "", price_delta: 0 }],
  required: true,
  max_selections: 1,
  help: "",
});

// Normalize a stored option value (string OR {label, price_delta}) to the
// form's editable shape: { label, price_delta }.
const normalizeFormValue = (v) => {
  if (typeof v === "string") return { _uid: _uid(), label: v, price_delta: 0 };
  if (v && typeof v === "object") {
    return {
      _uid: v._uid || _uid(),
      label: String(v.label ?? ""),
      price_delta: Number(v.price_delta) || 0,
    };
  }
  return { _uid: _uid(), label: "", price_delta: 0 };
};

/**
 * Edit-only modal for a single menu_items document.
 * Agency pricing is auto-calculated server-side (+20%), so this form only
 * surfaces the retail/private price.
 *
 * Props:
 *   open, onOpenChange
 *   item: existing menu item (required — edit-only, no create flow)
 *   categories: string[] available category names
 *   markup: number, e.g. 1.20
 *   onSaved: () => void
 */
export const MenuItemEditorModal = ({
  open,
  onOpenChange,
  item,
  categories = [],
  markup = 1.2,
  markupByCat = {},
  onSaved,
}) => {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        name: item.name || "",
        image: item.image || "",
        description: item.description || "",
        price: item.price != null ? String(item.price) : "",
        category: item.category || "",
        is_published: item.is_published !== false,
        quote_only: !!item.quote_only,
        options: Array.isArray(item.options)
          ? item.options.map((g) => ({
              _uid: g._uid || _uid(),
              label: g.label || "",
              values:
                Array.isArray(g.values) && g.values.length > 0
                  ? g.values.map(normalizeFormValue)
                  : [{ _uid: _uid(), label: "", price_delta: 0 }],
              required: g.required !== false,
              max_selections: g.max_selections ?? 1,
              help: g.help || "",
            }))
          : [],
      });
    } else {
      setForm(emptyForm());
    }
    setError("");
    setConfirmingDelete(false);
    setDeleting(false);
  }, [open, item]);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  // ---- Option-group editors ----
  const updateGroup = (gi, key, val) =>
    setForm((s) => {
      const next = [...s.options];
      next[gi] = { ...next[gi], [key]: val };
      return { ...s, options: next };
    });
  const addGroup = () =>
    setForm((s) => ({ ...s, options: [...s.options, emptyGroup()] }));
  const removeGroup = (gi) =>
    setForm((s) => ({ ...s, options: s.options.filter((_, i) => i !== gi) }));
  const updateGroupValue = (gi, vi, key, val) =>
    setForm((s) => {
      const next = [...s.options];
      const vals = [...next[gi].values];
      vals[vi] = { ...vals[vi], [key]: val };
      next[gi] = { ...next[gi], values: vals };
      return { ...s, options: next };
    });
  const addGroupValue = (gi) =>
    setForm((s) => {
      const next = [...s.options];
      next[gi] = {
        ...next[gi],
        values: [...next[gi].values, { _uid: _uid(), label: "", price_delta: 0 }],
      };
      return { ...s, options: next };
    });
  const removeGroupValue = (gi, vi) =>
    setForm((s) => {
      const next = [...s.options];
      next[gi] = {
        ...next[gi],
        values: next[gi].values.filter((_, i) => i !== vi),
      };
      return { ...s, options: next };
    });
  const moveGroupValue = (gi, vi, dir) =>
    setForm((s) => {
      const next = [...s.options];
      const vals = [...next[gi].values];
      const swap = vi + dir;
      if (swap < 0 || swap >= vals.length) return s;
      [vals[vi], vals[swap]] = [vals[swap], vals[vi]];
      next[gi] = { ...next[gi], values: vals };
      return { ...s, options: next };
    });

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be 8MB or smaller.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/menu/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = res.data.url.startsWith("http")
        ? res.data.url
        : `${apiBase}${res.data.url}`;
      setForm((s) => ({ ...s, image: fullUrl }));
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Upload failed"
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.category) return "Category is required.";
    const p = parseFloat(form.price);
    if (Number.isNaN(p) || p < 0) return "Price must be a non-negative number.";
    for (const [gi, g] of form.options.entries()) {
      if (!g.label.trim())
        return `Option group ${gi + 1}: label is required.`;
      const cleaned = g.values
        .map((v) => ({ label: (v.label || "").trim(), price_delta: Number(v.price_delta) || 0 }))
        .filter((v) => v.label);
      if (cleaned.length === 0)
        return `Option group "${g.label}": needs at least one value.`;
      const dupes = cleaned.length !== new Set(cleaned.map((v) => v.label.toLowerCase())).size;
      if (dupes) return `Option group "${g.label}": values must be unique.`;
      for (const v of cleaned) {
        if (!Number.isFinite(v.price_delta))
          return `Option group "${g.label}": price for "${v.label}" must be a number.`;
      }
      const max = Number(g.max_selections);
      if (!Number.isFinite(max) || max < 1)
        return `Option group "${g.label}": max selections must be 1 or more.`;
      if (max > cleaned.length)
        return `Option group "${g.label}": max selections (${max}) can't exceed the number of values (${cleaned.length}).`;
    }
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSaving(true);
    // Normalize option groups for the API. Each value becomes a clean object
    // {label, price_delta} so backend pricing always finds the delta.
    const normalizedOptions = form.options.map((g) => {
      const values = g.values
        .map((v) => ({
          label: (v.label || "").trim(),
          price_delta: Math.round((Number(v.price_delta) || 0) * 100) / 100,
        }))
        .filter((v) => v.label);
      return {
        label: g.label.trim(),
        values,
        required: !!g.required,
        max_selections: Math.min(Math.max(1, Number(g.max_selections) || 1), values.length),
        help: g.help?.trim() || "",
      };
    });
    try {
      await api.patch(`/admin/menu/${item.id}`, {
        name: form.name,
        image: form.image,
        description: form.description,
        price: parseFloat(form.price),
        category: form.category,
        is_published: form.is_published,
        quote_only: !!form.quote_only,
        options: normalizedOptions,
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not save"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item?.id) return;
    setError("");
    setDeleting(true);
    try {
      await api.delete(`/admin/menu/${item.id}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not delete"
      );
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const priceNum = parseFloat(form.price);
  // Beverages (and any other category with a custom markup) apply a different
  // agency multiplier — resolve based on the currently-selected form category.
  const effMarkup =
    (form.category && markupByCat[form.category]) || markup;
  const agencyPreview =
    !Number.isNaN(priceNum) && priceNum >= 0
      ? (priceNum * effMarkup).toFixed(2)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="menu-editor-modal"
        className="sm:max-w-[680px] max-h-[92vh] overflow-y-auto p-0 rounded-2xl border-none"
      >
        <div className="bg-brand-charcoal text-white px-6 py-5 sticky top-0 z-10">
          <DialogHeader className="space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
              Edit Menu Item
            </p>
            <DialogTitle className="font-display font-black text-[22px] tracking-tight text-white text-left">
              {form.name || item?.name || "Menu Item"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-white/65 text-left">
              Saves go live on /menu instantly. Agency price is auto-calculated
              at +{Math.round((effMarkup - 1) * 100)}%
              {form.category && markupByCat[form.category]
                ? ` (${form.category})`
                : ""}
              .
            </DialogDescription>
            {item?.id && <RecipeLink itemId={item.id} />}
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          {/* Image */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-[14px] text-brand-charcoal">
                Photo
              </h3>
              <button
                type="button"
                data-testid="menu-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {uploading ? "Uploading…" : "Upload Photo"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="menu-file-input"
              />
            </div>
            {form.image ? (
              <div
                data-testid="menu-image-preview"
                className="relative aspect-[16/10] rounded-xl overflow-hidden bg-white border border-black/5"
              >
                <img
                  src={form.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-black/15 p-8 text-center text-[12.5px] text-brand-charcoal/60">
                <ImageIcon className="h-6 w-6 mx-auto mb-2 text-brand-charcoal/30" />
                No photo yet. Upload one or paste a URL below.
              </div>
            )}
            <div className="mt-3">
              <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                Image URL
              </Label>
              <Input
                data-testid="menu-image-url"
                value={form.image}
                onChange={update("image")}
                placeholder="https://…"
                className="mt-1.5 h-10 rounded-xl bg-white border-black/10 text-[13px]"
              />
            </div>
          </section>

          {/* Core fields */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Field label="Name *">
                <Input
                  data-testid="menu-name"
                  value={form.name}
                  onChange={update("name")}
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Description">
                <Textarea
                  data-testid="menu-description"
                  value={form.description}
                  onChange={update("description")}
                  rows={3}
                  className="rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <Field label="Category *">
              <select
                data-testid="menu-category"
                value={form.category}
                onChange={update("category")}
                className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px]"
              >
                <option value="">Choose…</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Retail Price (Private) *">
              <Input
                data-testid="menu-price"
                value={form.price}
                onChange={update("price")}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                className="h-10 rounded-xl bg-white border-black/10 tabular-nums"
              />
            </Field>
            {agencyPreview && (
              <div className="sm:col-span-2 -mt-1 text-[11.5px] text-brand-charcoal/60">
                Agency price (auto, +{Math.round((effMarkup - 1) * 100)}%):{" "}
                <span className="font-bold text-brand-charcoal tabular-nums">
                  ${agencyPreview}
                </span>
              </div>
            )}
          </section>

          <div className="flex items-center gap-2 px-1">
            <input
              id="menu-publish"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) =>
                setForm((s) => ({ ...s, is_published: e.target.checked }))
              }
              data-testid="menu-published"
              className="h-4 w-4 accent-brand-teal"
            />
            <label
              htmlFor="menu-publish"
              className="text-[12.5px] text-brand-charcoal/75"
            >
              Published — visible on /menu and bookable for orders
            </label>
          </div>

          <div className="flex items-start gap-2 px-1">
            <input
              id="menu-quote-only"
              type="checkbox"
              checked={!!form.quote_only}
              onChange={(e) =>
                setForm((s) => ({ ...s, quote_only: e.target.checked }))
              }
              data-testid="menu-quote-only"
              className="mt-0.5 h-4 w-4 accent-brand-teal"
            />
            <label
              htmlFor="menu-quote-only"
              className="text-[12.5px] text-brand-charcoal/75"
            >
              Quote-only pricing — customers see a Request Quote button instead of the price.
              The price above stays internal as your default.
            </label>
          </div>

          {/* Option groups (modifiers like sauce / flavor / cheese with optional upcharges) */}
          <section
            data-testid="menu-options-section"
            className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-display font-black text-[14px] text-brand-charcoal">
                  Modifier Groups
                </h3>
                <p className="text-[11.5px] text-brand-charcoal/55 leading-snug">
                  Add an option group (e.g. <em>Sauce</em>, <em>Slider Style</em>, <em>Cheese</em>) and the
                  customer is asked to choose before adding to cart. Use <strong>price delta</strong> to
                  upcharge specific picks (e.g. Salmon +$5).
                </p>
              </div>
              <button
                type="button"
                onClick={addGroup}
                data-testid="menu-options-add-group"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Group
              </button>
            </div>
            {form.options.length === 0 ? (
              <p className="text-[12.5px] text-brand-charcoal/55 italic">
                No modifier groups. Customers add this item directly to cart with a simple quantity stepper.
              </p>
            ) : (
              <ul className="space-y-3">
                {form.options.map((g, gi) => (
                  <li
                    key={g._uid}
                    data-testid={`menu-options-group-${gi}`}
                    className="rounded-xl bg-white border border-black/10 p-3"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid sm:grid-cols-2 gap-2">
                        <Field label="Group Label *">
                          <Input
                            data-testid={`menu-options-group-${gi}-label`}
                            value={g.label}
                            onChange={(e) => updateGroup(gi, "label", e.target.value)}
                            placeholder="Sauce, Flavor, Cheese…"
                            className="h-9 rounded-lg bg-white border-black/10 text-[13px]"
                          />
                        </Field>
                        <Field label="Max selections">
                          <Input
                            data-testid={`menu-options-group-${gi}-max`}
                            value={g.max_selections}
                            onChange={(e) =>
                              updateGroup(gi, "max_selections", Number(e.target.value) || 1)
                            }
                            type="number"
                            min="1"
                            step="1"
                            className="h-9 rounded-lg bg-white border-black/10 text-[13px] tabular-nums"
                          />
                        </Field>
                        <div className="sm:col-span-2">
                          <Field label="Help text (shown in modal)">
                            <Input
                              data-testid={`menu-options-group-${gi}-help`}
                              value={g.help}
                              onChange={(e) => updateGroup(gi, "help", e.target.value)}
                              placeholder="Pick one for the dozen."
                              className="h-9 rounded-lg bg-white border-black/10 text-[13px]"
                            />
                          </Field>
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2">
                          <input
                            id={`menu-options-group-${gi}-required`}
                            type="checkbox"
                            checked={!!g.required}
                            onChange={(e) => updateGroup(gi, "required", e.target.checked)}
                            data-testid={`menu-options-group-${gi}-required`}
                            className="h-3.5 w-3.5 accent-brand-teal"
                          />
                          <label
                            htmlFor={`menu-options-group-${gi}-required`}
                            className="text-[12px] text-brand-charcoal/75"
                          >
                            Required — customer must pick a value to add to cart
                          </label>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeGroup(gi)}
                        data-testid={`menu-options-group-${gi}-remove`}
                        aria-label="Remove group"
                        className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Values within this group */}
                    <div className="mt-3 pt-3 border-t border-black/5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                          Choices ({g.values.length})
                        </p>
                        <button
                          type="button"
                          onClick={() => addGroupValue(gi)}
                          data-testid={`menu-options-group-${gi}-add-value`}
                          className="inline-flex items-center gap-1 rounded-full bg-brand-cream text-brand-charcoal px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-gold transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          Add Choice
                        </button>
                      </div>
                      <ul className="space-y-1.5">
                        {g.values.map((v, vi) => (
                          <li
                            key={v._uid}
                            data-testid={`menu-options-group-${gi}-value-${vi}`}
                            className="flex items-center gap-2"
                          >
                            <div className="flex flex-col gap-0.5">
                              <button
                                type="button"
                                onClick={() => moveGroupValue(gi, vi, -1)}
                                aria-label="Move up"
                                disabled={vi === 0}
                                className="h-4 w-4 inline-flex items-center justify-center text-brand-charcoal/50 hover:text-brand-charcoal disabled:opacity-30"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveGroupValue(gi, vi, 1)}
                                aria-label="Move down"
                                disabled={vi === g.values.length - 1}
                                className="h-4 w-4 inline-flex items-center justify-center text-brand-charcoal/50 hover:text-brand-charcoal disabled:opacity-30"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>
                            </div>
                            <Input
                              data-testid={`menu-options-group-${gi}-value-${vi}-label`}
                              value={v.label}
                              onChange={(e) =>
                                updateGroupValue(gi, vi, "label", e.target.value)
                              }
                              placeholder="Salmon, Pepper Jack, Coke…"
                              className="flex-1 h-9 rounded-lg bg-white border-black/10 text-[13px]"
                            />
                            <div className="inline-flex items-center gap-1 shrink-0">
                              <span className="text-[11px] text-brand-charcoal/55 tabular-nums">+$</span>
                              <Input
                                data-testid={`menu-options-group-${gi}-value-${vi}-delta`}
                                value={v.price_delta}
                                onChange={(e) =>
                                  updateGroupValue(
                                    gi,
                                    vi,
                                    "price_delta",
                                    e.target.value === ""
                                      ? 0
                                      : Math.max(0, Number(e.target.value))
                                  )
                                }
                                type="number"
                                inputMode="decimal"
                                step="0.50"
                                min="0"
                                placeholder="0"
                                className="w-20 h-9 rounded-lg bg-white border-black/10 text-[13px] tabular-nums"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeGroupValue(gi, vi)}
                              data-testid={`menu-options-group-${gi}-value-${vi}-remove`}
                              aria-label="Remove choice"
                              disabled={g.values.length === 1}
                              className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error && (
            <div
              data-testid="menu-error"
              role="alert"
              className="inline-flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              data-testid="menu-save-btn"
              onClick={submit}
              disabled={saving || uploading || deleting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream disabled:opacity-60"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>

          {item?.id && (
            <div
              data-testid="menu-delete-zone"
              className="mt-4 rounded-2xl border border-red-200 bg-red-50/60 px-4 py-3.5"
            >
              {!confirmingDelete ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <div>
                    <p className="text-[12.5px] font-bold text-red-800">
                      Delete this item
                    </p>
                    <p className="text-[11.5px] text-red-700/80 leading-snug">
                      Removes it from /menu, the picker, and the PDF brochure.
                      Past orders keep their line items.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="menu-delete-btn"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={saving || deleting}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-red-300 text-red-700 px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-red-100 disabled:opacity-60 shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete item
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[12.5px] font-bold text-red-800">
                    Delete &ldquo;{item.name}&rdquo; permanently?
                  </p>
                  <p className="text-[11.5px] text-red-700/85 leading-snug">
                    This cannot be undone. The item will disappear from the
                    live menu, admin picker, and PDF brochure immediately.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      data-testid="menu-delete-confirm-btn"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-red-600 text-white px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-red-700 transition-colors disabled:opacity-60"
                    >
                      {deleting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Yes, delete permanently
                    </button>
                    <button
                      type="button"
                      data-testid="menu-delete-cancel-btn"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deleting}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
      {label}
    </Label>
    {children}
  </div>
);

export default MenuItemEditorModal;
